import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, FlaskConical, Hospital, FileText, CalendarClock, Bell, MapPin, Loader2, Check, Tag, Pill, Sparkles, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { bookingApi, consultationApi, healthApi, referralApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

// Quick actions 2x2 — moved from Home ("What do you need today?")
const QUICK = [
  { id: "find-hospital", label: "Find Hospital", Icon: Hospital, box: "#CCFBF1", color: "#005C66", to: "/find-care" },
  { id: "doctor", label: "Find Doctor", Icon: Stethoscope, box: "#DBEAFE", color: "#2563EB", to: "/doctors" },
  { id: "lab-tests", label: "Book Lab Test", Icon: FlaskConical, box: "#DCFCE7", color: "#16A34A", to: "/lab-booking" },
  { id: "medicines", label: "Buy Medicines", Icon: Pill, box: "#F3E8FF", color: "#9333EA", to: "/buy-medicine" },
];

const STATUS_STYLE = {
  pending: "bg-warning/15 text-warning",
  confirmed: "bg-success/15 text-success",
};

function StatusPill({ status }) {
  const s = String(status || "").toLowerCase();
  const cls = s.includes("confirm") ? STATUS_STYLE.confirmed : STATUS_STYLE.pending;
  return (
    <span className={`text-xs px-3 py-1 rounded-full shrink-0 ${cls}`}>
      {String(status).replace(/_/g, " ").toLowerCase()}
    </span>
  );
}

function BookingRow({ icon: Icon, title, subtitle, ref: refNo, status }) {
  return (
    <Card className="rounded-3xl p-4 flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-11 w-11 rounded-2xl bg-accent flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0">
          <p className="font-medium truncate">{title}</p>
          <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
          {refNo && <p className="text-xs text-muted-foreground mt-0.5">Ref: {refNo}</p>}
        </div>
      </div>
      <StatusPill status={status} />
    </Card>
  );
}

export default function ForYou() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ doctorBookings: [], labBookings: [], serviceBookings: [] });
  const [consults, setConsults] = useState([]);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    if (!user?._id) return;
    let active = true;
    Promise.all([
      bookingApi.all(user._id).catch(() => ({ data: {} })),
      consultationApi.listPatient(user._id).catch(() => ({ data: {} })),
      healthApi.records(user._id).catch(() => ({ data: {} })),
    ])
      .then(([b, c, r]) => {
        if (!active) return;
        setData(b.data || {});
        setConsults(c.data?.bookings || []);
        setRecords((r.data?.records || []).slice(0, 20));
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [user?._id]);

  const allBookings = [
    ...consults.map((c) => ({
      id: c.id,
      icon: Stethoscope,
      title: c.doctor_name || "Doctor consultation",
      subtitle: `${c.hospital_name || ""}${c.preferred_date ? " • " + c.preferred_date : ""}`,
      ref: c.booking_ref,
      status: c.status,
    })),
    ...(data.serviceBookings || []).map((s) => ({
      id: s.id,
      icon: Hospital,
      title: s.serviceName,
      subtitle: `${s.hospitalName || ""}${s.date ? " • " + new Date(s.date).toLocaleDateString() : ""}`,
      ref: s.referenceNumber,
      status: s.status,
    })),
    ...(data.labBookings || []).map((l) => ({
      id: l.id,
      icon: FlaskConical,
      title: l.labName || "Lab test",
      subtitle: l.date ? new Date(l.date).toLocaleDateString() : "",
      ref: l.referenceNumber,
      status: l.status,
    })),
  ];

  const pending = allBookings.filter((b) =>
    ["pending", "pending_hospital", "pending_doctor", "hospital_proposed_alternate"].includes(
      String(b.status).toLowerCase()
    )
  );

  const onQuick = (q) => (q.soon ? toast.info("Medicine ordering is coming to web soon.") : navigate(q.to));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold">For You</h1>
        <p className="text-muted-foreground mt-1">Your bookings, reports and to-dos.</p>
      </div>

      {/* Ask Dr Sanjeevani hero — moved from Home */}
      <button onClick={() => navigate("/assistant")} data-testid="dr-sanjeevani-hero" className="w-full rounded-3xl border border-border bg-card p-5 text-left hover:shadow-md transition-all">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#F3E8FF" }}>
            <Sparkles className="h-5 w-5" style={{ color: "#7C3AED" }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold">Ask Dr Sanjeevani</p>
            <p className="text-sm text-muted-foreground">Tell us your symptoms — get instant guidance</p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </div>
      </button>

      {/* Quick actions 2x2 — moved from Home */}
      <section>
        <h2 className="text-base font-semibold mb-3">What do you need today?</h2>
        <div className="grid grid-cols-2 gap-4">
          {QUICK.map((q) => (
            <button key={q.id} onClick={() => onQuick(q)} data-testid={`qa-${q.id}`} className="rounded-3xl border border-border bg-card p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: q.box }}>
                <q.Icon className="h-6 w-6" style={{ color: q.color }} />
              </div>
              <p className="mt-4 font-medium">{q.label}</p>
            </button>
          ))}
        </div>
      </section>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 rounded-3xl" />
          <Skeleton className="h-20 rounded-3xl" />
        </div>
      ) : (
        <Tabs defaultValue="bookings">
          <TabsList className="rounded-2xl">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-xl">
              Bookings
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports" className="rounded-xl">
              Reports
            </TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending" className="rounded-xl">
              Pending
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-4 space-y-3">
            {allBookings.length === 0 ? (
              <EmptyState icon={CalendarClock} text="No bookings yet." cta="Find care" onClick={() => navigate("/find-care")} />
            ) : (
              allBookings.map((b) => <BookingRow key={b.id} {...b} />)
            )}
          </TabsContent>

          <TabsContent value="reports" className="mt-4 space-y-3">
            {records.length === 0 ? (
              <EmptyState icon={FileText} text="No reports uploaded." cta="Upload a report" onClick={() => navigate("/records")} />
            ) : (
              records.map((r) => (
                <Card key={r._id} className="rounded-3xl p-4 flex items-center gap-3">
                  <div className="h-11 w-11 rounded-2xl bg-accent flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{r.documentName}</p>
                    <p className="text-sm text-muted-foreground truncate">
                      {r.patientName || "You"}
                      {r.uploadDate ? " • " + new Date(r.uploadDate).toLocaleDateString() : ""}
                    </p>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="pending" className="mt-4 space-y-3">
            {pending.length === 0 ? (
              <EmptyState icon={Bell} text="You're all caught up!" />
            ) : (
              pending.map((b) => <BookingRow key={b.id} {...b} />)
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, text, cta, onClick }) {
  return (
    <Card className="rounded-3xl p-8 text-center border-dashed">
      <Icon className="h-10 w-10 text-muted-foreground mx-auto" />
      <p className="mt-3 text-muted-foreground">{text}</p>
      {cta && (
        <Button className="mt-4 rounded-2xl" onClick={onClick}>
          {cta}
        </Button>
      )}
    </Card>
  );
}
